/**
 * @fileoverview Field layout primitives — label, description, error, grouping.
 *
 * Inspired by catalyst/fieldset but simplified for light-mode intake forms.
 * Uses data-slot pattern so parent layout can target children generically.
 */
import clsx from 'clsx';
import React from 'react';

// ── FormField ────────────────────────────────────────────────────────────────

interface FormFieldProps extends React.ComponentPropsWithoutRef<'div'> {
  /** When true shows a subtle error border highlight on the field wrapper */
  hasError?: boolean;
  /** When true shows a subtle indicator that the value came from CMS import */
  assisted?: boolean;
}

/**
 * Wraps a single label + control + optional description/error.
 * Adds consistent vertical spacing between children via data-slot selectors.
 * When `assisted`, a small "AI" badge appears next to the label.
 */
export const FormField = ({
  className,
  hasError,
  assisted,
  children,
  ...props
}: FormFieldProps): React.JSX.Element => {
  return (
    <div
      data-slot="field"
      data-assisted={assisted || undefined}
      className={clsx(
        className,
        '[&>[data-slot=label]+[data-slot=control]]:mt-1.5',
        '[&>[data-slot=label]+[data-slot=description]]:mt-0.5',
        '[&>[data-slot=description]+[data-slot=control]]:mt-1.5',
        '[&>[data-slot=control]+[data-slot=error]]:mt-1.5',
        '[&>[data-slot=control]+[data-slot=description]]:mt-1.5',
        hasError && 'animate-in fade-in duration-200',
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// ── FormLabel ────────────────────────────────────────────────────────────────

interface FormLabelProps extends React.ComponentPropsWithoutRef<'div'> {
  required?: boolean;
  htmlFor?: string;
}

export const FormLabel = ({
  className,
  required,
  children,
  htmlFor: _htmlFor,
  ...props
}: FormLabelProps): React.JSX.Element => {
  return (
    <div
      data-slot="label"
      className={clsx(
        className,
        'flex select-none items-center gap-1.5 text-sm font-medium text-zinc-800',
      )}
      {...props}
    >
      <span>
        {children}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      <AssistedBadge />
    </div>
  );
}

/**
 * Tiny "AI" pill that appears next to the label when the parent FormField
 * has `assisted` set. Uses CSS to show/hide based on parent data attribute
 * so the label component doesn't need its own prop.
 */
const AssistedBadge = (): React.JSX.Element => {
  return (
    <span
      className={clsx(
        'hidden',
        '[[data-assisted]_&]:inline-flex',
        'items-center rounded-full bg-blue-50 px-1.5 py-px',
        'text-[10px] font-semibold tracking-wide text-blue-600',
      )}
    >
      AI
    </span>
  );
}

// ── FormDescription ──────────────────────────────────────────────────────────

export const FormDescription = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'p'>): React.JSX.Element => {
  return (
    <p
      data-slot="description"
      className={clsx(className, 'text-xs text-zinc-500')}
      {...props}
    />
  );
}

// ── FormError ────────────────────────────────────────────────────────────────

export const FormError = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'p'>): React.JSX.Element | null => {
  if (!children) {
    return null;
  }

  return (
    <p
      data-slot="error"
      role="alert"
      className={clsx(className, 'text-xs text-red-600')}
      {...props}
    >
      {children}
    </p>
  );
}

// ── FormGroup ────────────────────────────────────────────────────────────────

/**
 * Vertical stack of FormField elements with consistent gap.
 */
export const FormGroup = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>): React.JSX.Element => {
  return <div data-slot="control" className={clsx(className, 'space-y-5')} {...props} />;
}

// ── FormSection ──────────────────────────────────────────────────────────────

interface FormSectionProps extends React.ComponentPropsWithoutRef<'section'> {
  /** Section heading */
  title?: string;
  /** Optional subtext below the heading */
  description?: string;
}

/**
 * Groups related fields under an optional heading.
 * Renders a subtle top border separator when not the first child.
 */
export const FormSection = ({
  title,
  description,
  className,
  children,
  ...props
}: FormSectionProps): React.JSX.Element => {
  return (
    <section className={clsx(className, 'space-y-5')} {...props}>
      {title && (
        <div className="space-y-0.5">
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          {description && <p className="text-xs text-zinc-500">{description}</p>}
        </div>
      )}
      {children}
    </section>
  );
}
